/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

export type Language = 'id' | 'en';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string, params?: Record<string, string>) => string;
  isLoading: boolean;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// Default language (Bahasa Indonesia)
const DEFAULT_LANGUAGE: Language = 'id';

// Language storage key
const LANGUAGE_STORAGE_KEY = 'adb-language';

// Helper function to get nested translation value
const getNestedValue = (obj: any, path: string): any => {
  return path.split('.').reduce((current, key) => {
    return current && current[key] !== undefined ? current[key] : undefined;
  }, obj);
};

// Helper function to replace placeholders in translation strings
const replacePlaceholders = (text: string, params?: Record<string, string>): string => {
  if (!params) return text;
  
  return Object.keys(params).reduce((result, key) => {
    return result.replace(new RegExp(`\\{${key}\\}`, 'g'), params[key]);
  }, text);
};

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguage] = useState<Language>(DEFAULT_LANGUAGE);
  const [translations, setTranslations] = useState<Record<string, any>>({});
  const [isLoading, setIsLoading] = useState(true);

  // Load language from localStorage or use default
  useEffect(() => {
    const savedLanguage = localStorage.getItem(LANGUAGE_STORAGE_KEY) as Language | null;
    if (savedLanguage && (savedLanguage === 'id' || savedLanguage === 'en')) {
      setLanguage(savedLanguage);
    }
    setIsLoading(false);
  }, []);

  // Load translations when language changes
  useEffect(() => {
    const loadTranslations = async () => {
      try {
        setIsLoading(true);
        const translationModule = await import(`../locales/${language}.json`);
        setTranslations(translationModule.default);
      } catch (error) {
        // Fallback to empty object
        setTranslations({});
      } finally {
        setIsLoading(false);
      }
    };

    if (!isLoading) {
      loadTranslations();
    }
  }, [language, isLoading]);

  // Save language to localStorage
  const handleSetLanguage = useCallback((lang: Language) => {
    setLanguage(lang);
    localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
  }, []);

  // Translation function
  const t = useCallback((key: string, params?: Record<string, string>): string => {
    if (isLoading) return '...';
    
    const value = getNestedValue(translations, key);
    
    if (value === undefined) {
      // Return key if translation not found
      return key;
    }
    
    if (typeof value === 'string') {
      return replacePlaceholders(value, params);
    }
    
    return String(value);
  }, [translations, isLoading]);

  const value: LanguageContextType = {
    language,
    setLanguage: handleSetLanguage,
    t,
    isLoading,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const useTranslation = (): LanguageContextType => {
  return useLanguage();
};
